import React from 'react';
import HighchartsReact from 'highcharts-react-official';
import { isUndefined, isEqual, uniq, isArray, sortBy } from 'lodash';

import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import coalesce from 'src/utils/Functions/Coalesce';
import Renderer from 'src/utils/Domain/Renderer';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { Options, TooltipFormatterContextObject, SeriesPieOptions, PointLegendItemClickEventObject, Point } from 'highcharts';
import type { SimplerChartConfig } from 'configuration-schemas/viewdefns/viewdefn';
import Highcharts from 'highcharts';
import { SimpleChartProps } from './SimplerChart';

export class SimplerPieChart extends React.Component<SimpleChartProps> {
  constructor(props: SimpleChartProps) {
    super(props);
  }

  shouldComponentUpdate(nextProps: SimpleChartProps) {
    const { data: oldData, config: oldConfig } = this.props;

    if (!isUndefined(oldData) && !isUndefined(nextProps.data)) {
      const newData = nextProps.data;
      const newConfig = nextProps.config;

      const isDifferentData = !isEqual(oldData, newData);
      const isDifferentConfig = !isEqual(oldConfig, newConfig);

      return isDifferentData || isDifferentConfig;
    }
    return true;
  }

  dataHandle(data: BasicPivotItem[], config: SimplerChartConfig, title: string | undefined): Options {
    /* eslint-disable-next-line @typescript-eslint/no-this-alias */
    const that = this;
    const xAxisDataIndex = config.dataIndex || 'month';
    const xAxisSortIndex = config.sortIndex || xAxisDataIndex;
    const xAxisTitle = config.title ?? config.text ?? 'Month';
    // alphanum sort them by xAxisSortIndex
    const sortedData = sortBy(data, xAxisSortIndex);
    let categories: string[] = sortedData.map((m) => m[xAxisDataIndex]);

    // The incoming categories *should* be unique
    categories = uniq(categories);

    return {
      title: {
        text: coalesce(title, config.text), // Comes from selectedItem
      },
      xAxis: {
        categories: categories,
        title: {
          text: xAxisTitle,
        },
        visible: false,
      },
      tooltip: {
        formatter: function (this: TooltipFormatterContextObject) {
          if (that.props.config != null) {
            // @ts-ignore custom point prop
            return `${this.point.name}<br/>${Renderer.renderJustValue(this.point.y, this.point.view)}`;
          }
        },
      },
      legend: {
        enabled: true,
        maxHeight: 70,
        margin: 10,
        itemStyle: {
          fontSize: '11px',
        },
      },
      plotOptions: {
        pie: {
          innerSize: '70%',
          dataLabels: {
            enabled: false,
          },
          center: ['50%', '45%'],
          size: '70%',
          point: {
            events: {
              legendItemClick: function (this: Point, event: PointLegendItemClickEventObject) {
                event.preventDefault();
              },
            },
          },
        },

      },
      series: this.getSeries(sortedData, categories, config),
      credits: {
        enabled: false,
      },
      chart: {
        type: 'pie',
        reflow: true,
        spacingBottom: 20,
      },
    };
  }

  getSeries = (data: BasicPivotItem[], categories: string[], config: TenantConfigViewItem): SeriesPieOptions[] => {
    // If multiple series are configured, render multiple donuts
    const seriesCfgs = isArray(config.view) && config.view.length > 0 ? config.view : [config];

    return seriesCfgs.map((series, idx) => {
      // to match PieRender.tsx - same type of donut
      const center = seriesCfgs.length > 1 ? `${16.5 + 33 * idx}%` : '50%';

      return {
        type: 'pie',
        name: series.text,
        center: [center, '50%'],
        innerSize: '70%',
        showInLegend: true,
        dataLabels: {
          enabled: false,
        },
        data: categories.map((category) => {
          const row = data.find((d) => d[config.dataIndex] === category);
          return {
            name: category,
            y: row?.[series.dataIndex],
            view: series.bubble ?? series,
          };
        }),
      };
    });
  };

  render() {
    const { data, config, title } = this.props;
    if (data == null || config == null || this.props.config == null) {
      return <div />;
    }

    const highchartsConfig = this.dataHandle(data, config, title);

    return (
      <HighchartsReact
        highcharts={Highcharts}
        containerProps={{ style: { height: '100%' } }}
        options={{ ...highchartsConfig }}
        immutable={true}
      />
    );
  }
}
